<script setup lang="ts">
import { useDeviceConnectionState } from './utils/useDeviceConnectionState';
import { I18nKey } from '@/utils/typings';
import { IotDeviceStateOfUse } from '@/api/generator';
import { mdiCircle } from '@mdi/js';
import { computed } from 'vue';

const props = defineProps<{
  value?: IotDeviceStateOfUse;
}>();

const info = useDeviceConnectionState(computed(() => props.value));
// Circle states render as a CSS dot with a white outline (readable on the
// coloured status bar, the outline simply disappears on light surfaces);
// non-circle states (the error alert) stay a plain coloured icon.
const isDot = computed(() => info.value.icon === mdiCircle);
</script>

<template>
  <div class="device-connection-state d-flex align-center">
    <span
      v-if="isDot"
      class="state-dot"
      :style="{ backgroundColor: `rgb(var(--v-theme-${info.color}))` }"
    />
    <v-icon v-else :color="info.color" size="16">{{ info.icon }}</v-icon>
    <div>{{ $t(info.label as I18nKey) }}</div>
  </div>
</template>

<style lang="scss" scoped>
.device-connection-state {
  padding: 2px;
  gap: 4px;

  .state-dot {
    flex: 0 0 auto;
    width: 14px;
    height: 14px;
    border-radius: 50%;
    // 1px to match the battery icon's 1px drop-shadow outline next to it.
    border: 1px solid #fff;
    // Soft ring outside the white border so the white offline dot stays
    // visible on white surfaces (table rows, tiles).
    box-shadow: 0 0 2px rgba(0, 0, 0, 0.35);
  }
}
</style>
